/**
 * ข้อมูลจำลองสำหรับหน้าจอกลุ่มคิวตรวจสอบคำขอสะสมคะแนน
 *
 * ที่นี่คือแหล่งข้อมูลจำลองแห่งเดียวของกลุ่มหน้าจอนี้ — ไม่มีการเก็บลง localStorage
 * (localStorage ใช้เฉพาะการจำค่าที่ผู้ใช้ตั้งเอง เช่น ความกว้างคอลัมน์ ผ่าน usePersistentState)
 *
 * ตัวเลขถูกวางให้สมจริงตามข้อมูลใน PROGRESS-REPORT ข้อ 3:
 * — คำขอจากสลิปเป็นส่วนใหญ่ของคิวคน · คำขอจาก Order ID ส่วนใหญ่ระบบตรวจได้เอง
 * — SLA ที่แจ้งลูกค้าคือ 1–3 วันทำการ
 */

import type { ClaimSource, QueueStatus } from '../components';

/**
 * ช่องทางการซื้อ
 *
 * ⚠️ **ไม่ใช่รายการปิด** — ของจริงมาจาก `order_location` ของ CRM ซึ่งเป็นข้อความอิสระ
 *    ที่ลูกค้าเลือกในแอปมือถือ (แอปส่ง channelLabel มาเก็บที่นี่)
 *    ค่าที่ระบุไว้เป็นค่าที่พบบ่อย มีไว้ให้ autocomplete ช่วยตอนเขียนโค้ด
 *    ส่วน `(string & {})` ทำให้รับค่าที่ยังไม่รู้จักได้โดยไม่เสีย autocomplete
 */
export type Channel =
  | 'Shopee' | 'Lazada' | 'TikTok Shop' | 'ร้านขายยา' | 'บูธ' | 'อื่น ๆ'
  | (string & {});
export type ClaimKind = 'slip' | 'order-id';
/** ชื่อระดับสมาชิก — เป็นข้อความอิสระตามที่ตั้งไว้ในระบบ ไม่ได้มีแค่สามชื่อ */
export type Tier = string;

/** เหตุผลที่ระบบตรวจ Order ID ไม่ผ่าน */
export type AutoCheckFailure =
  | 'order-not-found'
  | 'unpaid'
  | 'already-claimed'
  | 'api-timeout';

export const AUTO_FAILURE_LABEL: Record<AutoCheckFailure, string> = {
  'order-not-found': 'ไม่พบคำสั่งซื้อนี้ในระบบของแพลตฟอร์ม',
  unpaid: 'คำสั่งซื้อยังไม่ชำระเงิน',
  'already-claimed': 'เลขคำสั่งซื้อนี้ถูกใช้สะสมคะแนนไปแล้ว',
  'api-timeout': 'แพลตฟอร์มไม่ตอบสนอง — ระบบจะลองใหม่อัตโนมัติ',
};

/** ลิงก์เปิดหน้าคำสั่งซื้อฝั่งผู้ขาย — ใช้ตอนที่ยังไม่เชื่อม API และเจ้าหน้าที่ต้องตรวจเอง */
export const PLATFORM_ORDER_URL: Partial<Record<Channel, string>> = {
  Shopee: 'https://seller.shopee.co.th/portal/sale/order?searchKeyword=',
  Lazada: 'https://sellercenter.lazada.co.th/apps/order/list?orderNumber=',
  'TikTok Shop': 'https://seller-th.tiktok.com/order/detail?order_no=',
};

export interface MemberSummary {
  id: string;
  name: string;
  memberCode: string;
  tier: Tier;
  points: number;
  /** ยอดสะสมในรอบปีคะแนนปัจจุบัน (บาท) */
  yearSpend: number;
  joinedAt: string;
  /** ประวัติคำขอ — เจ้าหน้าที่ต้องเห็นบริบทก่อนตัดสิน */
  claimsTotal: number;
  claimsApproved: number;
  claimsRejected: number;
  /** จำนวนคำขอที่ส่งมาใน 24 ชั่วโมงล่าสุด (เพดานต่อวันยังไม่ยืนยัน — ดูประเด็นข้อ ง) */
  claimsToday: number;
}

export interface AnomalyFlag {
  kind: 'duplicate-slip' | 'high-frequency' | 'amount-outlier' | 'amount-mismatch';
  /** ข้อความที่เจ้าหน้าที่อ่านแล้วรู้ว่าต้องทำอะไรต่อ */
  message: string;
  /** รหัสคำขอที่เกี่ยวข้อง (กรณีสลิปซ้ำ) */
  relatedClaimCode?: string;
}

/** รายการสินค้าที่ระบบอ่านจากภาพสลิป (OCR) — อ่านอัตโนมัติ อาจผิด */
export interface ReceiptScanLine {
  name: string;
  qty: number;
  /** ราคารวมของบรรทัดนี้ (บาท) */
  amount: number;
}

export interface ReceiptScan {
  /** [] = อ่านภาพแล้วแต่หารายการสินค้าไม่เจอ */
  items: ReceiptScanLine[];
  /** ยอดสุทธิที่อ่านได้ · null = หาไม่เจอ */
  total: number | null;
}

export interface Claim {
  id: string;
  code: string;
  kind: ClaimKind;
  status: QueueStatus;
  source: ClaimSource;
  channel: Channel;
  member: MemberSummary;
  /** ยอดที่ลูกค้ากรอกเอง */
  declaredAmount: number;
  /** ยอดที่เจ้าหน้าที่แก้ไขแล้ว (ถ้ามี) */
  verifiedAmount?: number;
  /** ISO timestamp ที่ส่งคำขอ */
  submittedAt: string;
  /** ชั่วโมงที่คำขอค้างอยู่ในคิว */
  ageHours: number;
  slipUrl?: string;
  slipThumbUrl?: string;
  /** ผลอ่านสลิป · undefined = ระบบไม่ได้อ่าน (คำขอจากเลขคำสั่งซื้อ หรือไม่มีภาพ) */
  receiptScan?: ReceiptScan;
  orderNumber?: string;
  autoCheckFailure?: AutoCheckFailure;
  assigneeId: string | null;
  anomalies: AnomalyFlag[];
  /** ผลการตัดสิน (เมื่อ status = approved / rejected) */
  decision?: {
    at: string;
    byId: string;
    reasonCode?: string;
    reasonLabel?: string;
    detail?: string;
    pointsGranted?: number;
  };
}

export interface Staff {
  id: string;
  name: string;
  role: string;
  /** จำนวนที่ตรวจเสร็จวันนี้ */
  reviewedToday: number;
  /** เวลาเฉลี่ยต่อรายการ (วินาที) */
  avgSeconds: number;
  /** งานที่ถืออยู่ */
  assigned: number;
}

export const STAFF: Staff[] = [
  { id: 'u1', name: 'สุดารัตน์ ทองดี', role: 'เจ้าหน้าที่ตรวจสอบ', reviewedToday: 96, avgSeconds: 38, assigned: 41 },
  { id: 'u2', name: 'ณัฐพล วิริยะกุล', role: 'เจ้าหน้าที่ตรวจสอบ', reviewedToday: 74, avgSeconds: 52, assigned: 33 },
  { id: 'u3', name: 'พิมพ์ชนก อารีย์', role: 'เจ้าหน้าที่ตรวจสอบ', reviewedToday: 61, avgSeconds: 47, assigned: 28 },
  { id: 'u4', name: 'ธนกร แสงทวี', role: 'หัวหน้าทีมตรวจสอบ', reviewedToday: 22, avgSeconds: 71, assigned: 9 },
];

export const CURRENT_STAFF_ID = 'u1';

/** อัตราแลกคะแนน — ค่าจริงมาจากหน้ากติกาคะแนน (FR-ADM-24) */
/** อัตราสะสมคะแนน 100 บาท = 1 แต้ม ตาม SRS 3.4 — Admin ปรับได้ที่หน้ากติกาคะแนน (FR-ADM-24) */
export const BAHT_PER_POINT = 100;
export const pointsFor = (amount: number): number => Math.floor(amount / BAHT_PER_POINT);

/** SLA ที่แจ้งลูกค้า: 1–3 วันทำการ — เตือนเมื่อเหลือน้อยกว่า 1 วันทำการ */
export const SLA_HOURS = 72;
export const SLA_WARN_HOURS = 48;

const MEMBER_NAMES = [
  ['สุกัญญา พงษ์ไพบูลย์', 'Gold'], ['อนุชา รัตนโกศล', 'Silver'], ['วราภรณ์ สุขเจริญ', 'Platinum'],
  ['กิตติศักดิ์ มณีรัตน์', 'Silver'], ['ปิยะดา ศรีวิชัย', 'Gold'], ['ชัยวัฒน์ เกียรติสกุล', 'Silver'],
  ['นภัสวรรณ อินทร์ทอง', 'Gold'], ['ธีรภัทร บุญมาก', 'Platinum'], ['ศิริพร ตั้งใจดี', 'Silver'],
  ['วิชญ์ ธนกิจ', 'Gold'], ['อรวรรณ พูนทรัพย์', 'Silver'], ['เจษฎา ประเสริฐผล', 'Gold'],
] as const;

const CHANNELS: Channel[] = ['Shopee', 'Lazada', 'TikTok Shop', 'ร้านขายยา', 'บูธ', 'อื่น ๆ'];

/** ภาพสลิปจำลอง — แถบเฉียงพร้อมคำกำกับ (ไม่ใช่ภาพจริง) */
const slipSvg = (label: string, w = 620, h = 980) =>
  `data:image/svg+xml;utf8,` +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><defs><pattern id="p" width="14" height="14" patternUnits="userSpaceOnUse"><path d="M0 14 L14 0" stroke="#C9CDD6" stroke-width="1"/></pattern></defs><rect width="${w}" height="${h}" fill="#F5F6F9"/><rect width="${w}" height="${h}" fill="url(#p)"/><rect x="40" y="40" width="${w - 80}" height="${h - 80}" fill="#FFFFFF" stroke="#C9CDD6"/><text x="${w / 2}" y="${h / 2}" text-anchor="middle" font-family="monospace" font-size="20" fill="#5B6270">${label}</text></svg>`,
  );

function member(i: number): MemberSummary {
  const [name, tier] = MEMBER_NAMES[i % MEMBER_NAMES.length]!;
  const approved = 4 + ((i * 3) % 22);
  const rejected = (i * 7) % 5;
  return {
    id: `m${i % 12}`,
    name,
    memberCode: `MB-${(4000 + (i % 12) * 137).toString().padStart(6, '0')}`,
    tier: tier as Tier,
    points: 180 + ((i * 311) % 9400),
    yearSpend: 4200 + ((i * 917) % 86000),
    joinedAt: `2567-0${(i % 9) + 1}-1${i % 9}`,
    claimsTotal: approved + rejected,
    claimsApproved: approved,
    claimsRejected: rejected,
    claimsToday: i % 11 === 0 ? 7 : (i % 4),
  };
}

function anomaliesFor(i: number, amount: number, kind: ClaimKind): AnomalyFlag[] {
  const out: AnomalyFlag[] = [];
  if (i % 23 === 0 && kind === 'slip') {
    out.push({
      kind: 'duplicate-slip',
      message: 'ภาพสลิปนี้เหมือนกับสลิปในคำขอที่อนุมัติไปแล้ว — เปิดเทียบก่อนตัดสิน',
      relatedClaimCode: `CLM-${69000 + i - 11}`,
    });
  }
  if (i % 11 === 0) {
    out.push({ kind: 'high-frequency', message: 'สมาชิกคนนี้ส่งคำขอ 7 ครั้งใน 24 ชั่วโมง สูงกว่าค่าปกติของกลุ่ม' });
  }
  if (amount > 7800) {
    out.push({ kind: 'amount-outlier', message: `ยอด ${amount.toLocaleString('th-TH')} บาท สูงกว่าค่าเฉลี่ยของช่องทางนี้ราว 6 เท่า` });
  }
  if (i % 17 === 0) {
    out.push({ kind: 'amount-mismatch', message: 'ระบบอ่านยอดในสลิปได้ไม่ตรงกับยอดที่ลูกค้ากรอก — ตรวจด้วยตาก่อน' });
  }
  return out;
}

const SCAN_PRODUCTS = [
  'Innobic Multivitamin 30 เม็ด',
  'Innobic NIGHT MODE GUMMIES',
  'อินโนบิก โพรไบโอติกส์ แอลดี-เลสส',
  'Innobic Collagen Plus 15 ซอง',
];

/**
 * ผลอ่านสลิปจำลอง — ส่วนใหญ่รวมรายการได้เท่ายอด · บางใบมีส่วนลดท้ายบิล (ยอดสุทธิน้อยกว่ารวมรายการ)
 * · บางใบอ่านรายการไม่ได้เลย ให้เห็นครบทุกสภาพที่หน้าจอต้องรับมือ
 */
function receiptScanFor(i: number, amount: number): ReceiptScan {
  if (i % 7 === 3) return { items: [], total: i % 2 ? amount : null };
  const lines = 1 + (i % 3);
  const items: ReceiptScanLine[] = [];
  let left = amount;
  for (let n = 0; n < lines; n += 1) {
    const last = n === lines - 1;
    const lineAmount = last ? left : Math.round(amount / lines);
    left -= lineAmount;
    items.push({ name: SCAN_PRODUCTS[(i + n) % SCAN_PRODUCTS.length]!, qty: 1 + ((i + n) % 2), amount: lineAmount });
  }
  /* ส่วนลดท้ายบิล — ยอดสุทธิไม่เท่ารวมรายการ
     i % 17 ตรงกับคำขอที่ anomaliesFor ติดธง "ยอดในสลิปไม่ตรงกับที่กรอก" — ให้ผลอ่านสอดคล้องกัน */
  const total = i % 17 === 0 ? amount - 120 : i % 6 === 1 ? amount - 50 : amount;
  return { items, total };
}

function buildClaims(): Claim[] {
  const out: Claim[] = [];
  const now = new Date('2026-08-21T09:30:00');

  for (let i = 0; i < 1240; i += 1) {
    const kind: ClaimKind = i % 5 === 0 ? 'order-id' : 'slip';
    const channel = (kind === 'order-id' ? CHANNELS[i % 3] : CHANNELS[3 + (i % 3)])!;
    const amount = 240 + ((i * 173) % 9200);

    /* สัดส่วนสถานะ: คิวรอตรวจเป็นส่วนใหญ่ ที่เหลือคือของที่ตัดสินแล้ว */
    let status: QueueStatus;
    if (i % 9 === 0) status = 'approved';
    else if (i % 13 === 0) status = 'rejected';
    else if (i % 29 === 0) status = 'review';
    else status = 'pending';

    const ageHours = status === 'pending' || status === 'review' ? (i * 7) % 96 : (i * 3) % 240;
    if (status === 'pending' && ageHours > SLA_HOURS) status = 'overdue';

    /* คำขอจาก Order ID: ส่วนใหญ่ระบบตรวจเองได้ ที่เหลือคือกรณีที่ระบบตรวจไม่ผ่าน */
    let source: ClaimSource = 'manual';
    let autoCheckFailure: AutoCheckFailure | undefined;
    if (kind === 'order-id') {
      if (i % 4 === 0) {
        source = 'failed';
        autoCheckFailure = (['order-not-found', 'unpaid', 'already-claimed', 'api-timeout'] as AutoCheckFailure[])[i % 4];
      } else {
        source = 'auto';
        status = i % 7 === 0 ? 'rejected' : 'approved';
      }
    }

    const submitted = new Date(now.getTime() - ageHours * 3600 * 1000);
    const m = member(i);

    out.push({
      id: `c${i}`,
      code: `CLM-${69000 + i}`,
      kind,
      status,
      source,
      channel,
      member: m,
      declaredAmount: amount,
      verifiedAmount: i % 19 === 0 ? amount - 120 : undefined,
      submittedAt: submitted.toISOString(),
      ageHours,
      slipUrl: kind === 'slip' ? slipSvg(`สลิป ${channel} · ${amount.toLocaleString('th-TH')} บาท`) : undefined,
      slipThumbUrl: kind === 'slip' ? slipSvg('สลิป', 96, 128) : undefined,
      receiptScan: kind === 'slip' ? receiptScanFor(i, amount) : undefined,
      orderNumber: kind === 'order-id' ? `${channel === 'Shopee' ? '2408' : channel === 'Lazada' ? '7712' : '9931'}${(100000 + i * 37).toString()}` : undefined,
      autoCheckFailure,
      assigneeId: status === 'pending' || status === 'overdue' ? (i % 3 === 0 ? CURRENT_STAFF_ID : STAFF[i % STAFF.length]!.id) : STAFF[i % STAFF.length]!.id,
      anomalies: anomaliesFor(i, amount, kind),
      decision:
        status === 'approved' || status === 'rejected'
          ? {
              at: new Date(submitted.getTime() + 5 * 3600 * 1000).toISOString(),
              byId: source === 'auto' ? 'system' : STAFF[i % STAFF.length]!.id,
              reasonCode: status === 'rejected' ? 'unreadable' : undefined,
              reasonLabel: status === 'rejected' ? 'รูปสลิปไม่ชัด อ่านยอดเงินหรือวันที่ไม่ได้ กรุณาถ่ายใหม่' : undefined,
              pointsGranted: status === 'approved' ? pointsFor(amount) : 0,
            }
          : undefined,
    });
  }
  return out;
}

export const CLAIMS: Claim[] = buildClaims();

/** ประวัติการกระทำของคำขอหนึ่งใบ — แสดงในแผงรายละเอียด (FR-ADM-37) */
export function auditFor(claim: Claim) {
  const entries = [
    {
      id: `${claim.id}-submit`,
      at: claim.submittedAt,
      actorName: claim.member.name,
      actorRole: 'สมาชิก',
      action: claim.kind === 'slip' ? 'ส่งคำขอสะสมคะแนนจากสลิป' : 'ส่งคำขอสะสมคะแนนจากเลขคำสั่งซื้อ',
      icon: 'upload' as const,
      changes: [
        { field: 'ยอดที่กรอก', before: null, after: `${claim.declaredAmount.toLocaleString('th-TH')} บาท` },
        { field: 'ช่องทาง', before: null, after: claim.channel },
      ],
    },
  ];

  if (claim.source === 'auto' || claim.source === 'failed') {
    entries.push({
      id: `${claim.id}-auto`,
      at: new Date(new Date(claim.submittedAt).getTime() + 40000).toISOString(),
      actorName: 'ระบบ',
      actorRole: '—',
      action:
        claim.source === 'auto'
          ? `ตรวจเลขคำสั่งซื้อกับ ${claim.channel} สำเร็จ`
          : `ตรวจเลขคำสั่งซื้อกับ ${claim.channel} ไม่ผ่าน — ${AUTO_FAILURE_LABEL[claim.autoCheckFailure ?? 'api-timeout']}`,
      icon: 'bolt' as const,
      automated: true,
      changes: [],
    } as never);
  }

  if (claim.verifiedAmount !== undefined) {
    entries.push({
      id: `${claim.id}-amend`,
      at: new Date(new Date(claim.submittedAt).getTime() + 3600000).toISOString(),
      actorName: STAFF[0]!.name,
      actorRole: STAFF[0]!.role,
      action: 'แก้ไขยอดเงินให้ตรงกับสลิป',
      icon: 'edit' as const,
      changes: [
        {
          field: 'ยอดที่ใช้คำนวณคะแนน',
          before: `${claim.declaredAmount.toLocaleString('th-TH')} บาท`,
          after: `${claim.verifiedAmount.toLocaleString('th-TH')} บาท`,
        },
        {
          field: 'คะแนนที่จะได้',
          before: `${pointsFor(claim.declaredAmount)}`,
          after: `${pointsFor(claim.verifiedAmount)}`,
        },
      ],
    } as never);
  }

  if (claim.decision) {
    const by = STAFF.find((s) => s.id === claim.decision!.byId);
    entries.push({
      id: `${claim.id}-decide`,
      at: claim.decision.at,
      actorName: by?.name ?? 'ระบบ',
      actorRole: by?.role ?? '—',
      action: claim.status === 'approved' ? 'อนุมัติคำขอ' : 'ปฏิเสธคำขอ',
      icon: claim.status === 'approved' ? ('check_circle' as const) : ('cancel' as const),
      automated: !by,
      reason: claim.decision.reasonLabel,
      changes: [{ field: 'สถานะ', before: 'รอตรวจสอบ', after: claim.status === 'approved' ? 'อนุมัติแล้ว' : 'ปฏิเสธ' }],
    } as never);
  }

  return entries.reverse();
}

/** ตัวชี้วัดของคิวงาน (FR-ADM-14) */
export const SLA_METRICS = {
  backlog: CLAIMS.filter((c) => c.status === 'pending' || c.status === 'review' || c.status === 'overdue').length,
  overdue: CLAIMS.filter((c) => c.status === 'overdue').length,
  nearDue: CLAIMS.filter((c) => c.status === 'pending' && c.ageHours >= SLA_WARN_HOURS && c.ageHours <= SLA_HOURS).length,
  avgReviewSeconds: 44,
  approvedToday: 253,
  rejectedToday: 31,
  autoApprovedToday: 1846,
  /** ปริมาณงานเข้าคิวรายวัน 14 วันล่าสุด (สลิป / Order ID ที่ต้องใช้คน) */
  daily: Array.from({ length: 14 }, (_, i) => ({
    date: `2569-08-${String(8 + i).padStart(2, '0')}`,
    slip: 180 + ((i * 37) % 120),
    orderId: 24 + ((i * 11) % 30),
    cleared: 170 + ((i * 41) % 130),
  })),
};

/* รองรับ: FR-ADM-10, FR-ADM-11, FR-ADM-12, FR-ADM-13, FR-ADM-14, FR-ADM-37 */
